import React, { createContext, useContext, useState } from 'react';

export interface Locale {
  code: string;
  name: string;
  flag: string;
  units: 'metric' | 'imperial';
  language: string;
}

export const LOCALES: Record<string, Locale> = {
  'en-US': {
    code: 'en-US',
    name: 'English',
    flag: '🇺🇸',
    units: 'imperial',
    language: 'en'
  },
  'de-DE': {
    code: 'de-DE',
    name: 'Deutsch',
    flag: '🇩🇪',
    units: 'metric',
    language: 'de'
  },
  'zh-CN': {
    code: 'zh-CN',
    name: '中文',
    flag: '🇨🇳',
    units: 'metric',
    language: 'zh'
  },
  'ja-JP': {
    code: 'ja-JP',
    name: '日本語',
    flag: '🇯🇵',
    units: 'metric',
    language: 'ja'
  }
};

// Translation keys and messages
const TRANSLATIONS: Record<string, Record<string, string>> = {
  en: {
    'nav.home': 'Home',
    'nav.addCarpet': 'Add Carpet',
    'nav.soldCarpets': 'Sold Carpets',
    'nav.signOut': 'Sign Out',
    'nav.signingOut': 'Signing Out...',
    'search.placeholder': 'Search carpets...',
    'search.noResults': 'No carpets found',
    'search.noResultsDesc': 'No carpets match your search',
    'search.noResultsFilter': 'No carpets match the current filters',
    'search.searching': 'Searching...',
    'search.clearFilters': 'Clear Filters',
    'size.all': 'All Sizes',
    'filter.allCategories': 'All Regions',
    'carpet.condition': 'Condition',
    'carpet.material': 'Material',
    'carpet.origin': 'Origin',
    'carpet.dimensions': 'Dimensions',
    'carpet.value': 'Value',
    'carpet.age': 'Age',
    'carpet.colors': 'Colors',
    'carpet.pattern': 'Pattern',
    'carpet.notes': 'Notes',
    'carpet.purchaseDate': 'Purchase Date',
    'carpet.length': 'Length',
    'carpet.width': 'Width',
    'carpet.internalId': 'Internal ID',
    'carpet.idLabel': 'ID',
    'carpet.sizeLabel': 'Size',
    'counter.rug': 'rug',
    'counter.rugs': 'rugs',
    'auth.title': 'MC53',
    'auth.subtitle': 'Curated collection of timeless carpets',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.noImage': 'No Image',
    'common.noImagesAvailable': 'No images available',
    'common.backToCollection': 'Back to Collection',
    'common.backToMainDirectory': 'Back to Main Directory',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.cancel': 'Cancel',
    'common.tryAgain': 'Try Again',
    'common.added': 'Added',
    'common.lastUpdated': 'Last updated',
    'units.metric': 'Metric',
    'units.imperial': 'Imperial',
    'status.sold': 'SOLD',
    'status.markAsSold': 'Mark as Sold',
    'status.markAsAvailable': 'Mark as Available',
    'status.soldCarpetsTitle': 'Sold Carpets',
    'status.soldCarpetsDesc': 'Browse carpets that have been sold',
    'status.noSoldCarpets': 'No Sold Carpets Found',
    'status.noSoldCarpetsDesc': 'No carpets have been marked as sold yet',
    'status.confirmSoldTitle': 'Mark as Sold',
    'status.confirmSoldMessage': 'Are you sure you want to mark "{name}" as sold? It will be hidden from the main directory and moved to the sold carpets page.',
    'status.confirmAvailableTitle': 'Mark as Available',
    'status.confirmAvailableMessage': 'Are you sure you want to mark "{name}" as available? It will appear in the main directory again.',
    'addCarpet.title': 'Add New Carpet',
    'addCarpet.backToCollection': 'Back to Collection',
    'addCarpet.carpetName': 'Carpet Name',
    'addCarpet.carpetNameRequired': 'Carpet name is required',
    'addCarpet.originRegion': 'Origin/Region',
    'addCarpet.originPlaceholder': 'e.g., Persian, Turkish, Afghan',
    'addCarpet.category': 'Category',
    'addCarpet.categoryHint': '(Primary region - extracted from name)',
    'addCarpet.selectCategory': 'Select category',
    'addCarpet.widthCm': 'Width (cm)',
    'addCarpet.widthPlaceholder': 'Width in cm',
    'addCarpet.lengthCm': 'Length (cm)',
    'addCarpet.lengthPlaceholder': 'Length in cm',
    'addCarpet.agePeriod': 'Age/Period',
    'addCarpet.agePlaceholder': 'e.g., Vintage, Antique, Modern',
    'addCarpet.selectCondition': 'Select condition',
    'addCarpet.conditionExcellent': 'Excellent',
    'addCarpet.conditionVeryGood': 'Very Good',
    'addCarpet.conditionGood': 'Good',
    'addCarpet.conditionFair': 'Fair',
    'addCarpet.conditionPoor': 'Poor',
    'addCarpet.valuePrice': 'Value/Price ($)',
    'addCarpet.valuePlaceholder': 'Enter value in USD',
    'addCarpet.purchaseDate': 'Purchase Date',
    'addCarpet.colorPalette': 'Color Palette',
    'addCarpet.colorPlaceholder': 'e.g., Red, Blue, Gold, Ivory',
    'addCarpet.patternDescription': 'Pattern Description',
    'addCarpet.patternPlaceholder': 'e.g., Geometric, Floral, Medallion',
    'addCarpet.images': 'Images',
    'addCarpet.clickToUpload': 'Click to upload images',
    'addCarpet.imageFormat': 'PNG, JPG, GIF up to 10MB each',
    'addCarpet.upload': 'Upload',
    'addCarpet.notesPlaceholder': 'Additional notes about the carpet...',
    'addCarpet.addingCarpet': 'Adding Carpet...',
    'addCarpet.addCarpet': 'Add Carpet',
    'addCarpet.internalId': 'Internal ID',
    'addCarpet.internalIdHint': '(For internal tracking only)',
    'addCarpet.internalIdPlaceholder': 'Internal reference ID',
    'addCarpet.materialPlaceholder': 'e.g., Wool, Silk, Cotton',
    'addCarpet.failedToCreate': 'Failed to create carpet',
    'edit.title': 'Edit Carpet',
    'edit.backToCarpet': 'Back to Carpet',
    'edit.notFound': 'Carpet not found',
    'edit.currentImages': 'Current Images',
    'edit.addNewImages': 'Add New Images',
    'edit.clickToUploadNew': 'Click to upload new images',
    'edit.saveChanges': 'Save Changes',
    'edit.savingChanges': 'Saving Changes...',
    'edit.failedToLoad': 'Failed to load carpet',
    'edit.failedToUpdate': 'Failed to update carpet',
    'edit.failedToDeleteImage': 'Failed to delete image',
    'search.searchingFor': 'Searching for "{value}"...',
    'export.title': 'Export',
    'export.pdf': 'Export as PDF',
    'export.csv': 'Export as CSV',
    'export.loading': 'Preparing export...',
    'export.error': 'Failed to export',
    'export.noCarpets': 'No carpets to export',
    'export.availableCarpets': 'Available Carpets',
    'export.soldCarpets': 'Sold Carpets',
    'export.generated': 'Generated',
    'export.page': 'Page',
    'export.of': 'of',
    'export.total': 'Total',
    'export.carpet': 'carpet',
    'export.carpets': 'carpets',
    'export.headerId': 'ID',
    'export.headerName': 'Name',
    'export.headerWidth': 'Width',
    'export.headerLength': 'Length',
    'export.headerSoldDate': 'Sold Date',
    'auth.emailLabel': 'Email address',
    'auth.emailPlaceholder': 'Enter your email',
    'auth.passwordLabel': 'Password',
    'auth.passwordPlaceholder': 'Enter your password',
    'auth.signIn': 'Sign in',
    'auth.signingIn': 'Signing in...',
    'auth.showPassword': 'Show password',
    'auth.hidePassword': 'Hide password',
    'auth.genericError': 'An error occurred',
    'delete.title': 'Delete Carpet',
    'delete.confirmMessage': 'Are you sure you want to delete "{name}"? This action cannot be undone.',
    'access.checkingPermissions': 'Checking permissions...',
    'access.deniedTitle': 'Access Denied',
    'access.deniedMessage': "You don't have permission to access this feature.",
    'access.deniedAdminOnly': 'Only administrators can perform this action.',
    'access.deniedContact': 'Please contact an administrator for access.',
    'access.currentRole': 'Current role',
    'nav.signOutError': 'Failed to sign out. Please try again.',
    'view.grid': 'Grid view',
    'view.list': 'List view',
    'filter.categoryLabel': 'Region',
    'filter.publicationLabel': 'Publication',
    'filter.publicationAll': 'All publications',
    'filter.publicationPublic': 'Public',
    'filter.publicationPrivate': 'Private',
    'search.clearSearch': 'Clear search',
    'error.loadingCarpets': 'Error Loading Carpets',
    'error.loadingSoldCarpets': 'Error Loading Sold Carpets',
    'size.small': 'Small (< 150cm)',
    'size.medium': 'Medium (150-300cm)',
    'size.large': 'Large (300-500cm)',
    'size.extraLarge': 'Extra Large (> 500cm)',
    'pagination.loadMore': 'Load More',
    'pagination.showingCount': 'Showing {shown} of {total}',
    'size.smallImperial': 'Small (< 4.9ft)',
    'size.mediumImperial': 'Medium (4.9-9.8ft)',
    'size.largeImperial': 'Large (9.8-16.4ft)',
    'size.extraLargeImperial': 'Extra Large (> 16.4ft)',
    'publish.badge': 'Public',
    'publish.publish': 'Publish',
    'publish.unpublish': 'Unpublish',
    'publish.confirmPublishTitle': 'Publish carpet',
    'publish.confirmPublishMessage': 'Publish "{name}" on the public website? It stays visible there while it remains unsold. The rest of the inventory stays private.',
    'publish.confirmUnpublishTitle': 'Unpublish carpet',
    'publish.confirmUnpublishMessage': 'Remove "{name}" from the public website? It stays in MC53.',
    'publish.failed': 'Failed to update publication',
    'publish.removedWhenSold': 'It will also leave the public website until it is available again.'
  },
  de: {
    'nav.home': 'Startseite',
    'nav.addCarpet': 'Teppich hinzufügen',
    'nav.soldCarpets': 'Verkaufte Teppiche',
    'nav.signOut': 'Abmelden',
    'nav.signingOut': 'Abmelden...',
    'search.placeholder': 'Teppiche suchen...',
    'search.noResults': 'Keine Teppiche gefunden',
    'search.noResultsDesc': 'Keine Teppiche entsprechen Ihrer Suche',
    'search.noResultsFilter': 'Keine Teppiche entsprechen den aktuellen Filtern',
    'search.searching': 'Suche läuft...',
    'search.clearFilters': 'Filter löschen',
    'size.all': 'Alle Größen',
    'filter.allCategories': 'Alle Regionen',
    'carpet.condition': 'Zustand',
    'carpet.material': 'Material',
    'carpet.origin': 'Herkunft',
    'carpet.dimensions': 'Abmessungen',
    'carpet.value': 'Wert',
    'carpet.age': 'Alter',
    'carpet.colors': 'Farben',
    'carpet.pattern': 'Muster',
    'carpet.notes': 'Notizen',
    'carpet.purchaseDate': 'Kaufdatum',
    'carpet.length': 'Länge',
    'carpet.width': 'Breite',
    'carpet.internalId': 'Interne ID',
    'carpet.idLabel': 'ID',
    'carpet.sizeLabel': 'Größe',
    'counter.rug': 'Teppich',
    'counter.rugs': 'Teppiche',
    'auth.title': 'MC53',
    'auth.subtitle': 'Kuratierte Sammlung zeitloser Teppiche',
    'common.loading': 'Lädt...',
    'common.error': 'Fehler',
    'common.noImage': 'Kein Bild',
    'common.noImagesAvailable': 'Keine Bilder verfügbar',
    'common.backToCollection': 'Zurück zur Sammlung',
    'common.backToMainDirectory': 'Zurück zum Hauptverzeichnis',
    'common.edit': 'Bearbeiten',
    'common.delete': 'Löschen',
    'common.cancel': 'Abbrechen',
    'common.tryAgain': 'Erneut versuchen',
    'common.added': 'Hinzugefügt',
    'common.lastUpdated': 'Zuletzt aktualisiert',
    'units.metric': 'Metrisch',
    'units.imperial': 'Imperial',
    'status.sold': 'VERKAUFT',
    'status.markAsSold': 'Als verkauft markieren',
    'status.markAsAvailable': 'Als verfügbar markieren',
    'status.soldCarpetsTitle': 'Verkaufte Teppiche',
    'status.soldCarpetsDesc': 'Durchsuchen Sie verkaufte Teppiche',
    'status.noSoldCarpets': 'Keine verkauften Teppiche gefunden',
    'status.noSoldCarpetsDesc': 'Es wurden noch keine Teppiche als verkauft markiert',
    'status.confirmSoldTitle': 'Als verkauft markieren',
    'status.confirmSoldMessage': 'Möchten Sie "{name}" wirklich als verkauft markieren? Er wird aus dem Hauptverzeichnis ausgeblendet und auf die Seite für verkaufte Teppiche verschoben.',
    'status.confirmAvailableTitle': 'Als verfügbar markieren',
    'status.confirmAvailableMessage': 'Möchten Sie "{name}" wirklich als verfügbar markieren? Er wird wieder im Hauptverzeichnis angezeigt.',
    'addCarpet.title': 'Neuen Teppich hinzufügen',
    'addCarpet.backToCollection': 'Zurück zur Sammlung',
    'addCarpet.carpetName': 'Teppichname',
    'addCarpet.carpetNameRequired': 'Teppichname ist erforderlich',
    'addCarpet.originRegion': 'Herkunft/Region',
    'addCarpet.originPlaceholder': 'z.B. Persisch, Türkisch, Afghanisch',
    'addCarpet.category': 'Kategorie',
    'addCarpet.categoryHint': '(Hauptregion - aus Name extrahiert)',
    'addCarpet.selectCategory': 'Kategorie auswählen',
    'addCarpet.widthCm': 'Breite (cm)',
    'addCarpet.widthPlaceholder': 'Breite in cm',
    'addCarpet.lengthCm': 'Länge (cm)',
    'addCarpet.lengthPlaceholder': 'Länge in cm',
    'addCarpet.agePeriod': 'Alter/Periode',
    'addCarpet.agePlaceholder': 'z.B. Vintage, Antik, Modern',
    'addCarpet.selectCondition': 'Zustand auswählen',
    'addCarpet.conditionExcellent': 'Ausgezeichnet',
    'addCarpet.conditionVeryGood': 'Sehr gut',
    'addCarpet.conditionGood': 'Gut',
    'addCarpet.conditionFair': 'Ordentlich',
    'addCarpet.conditionPoor': 'Schlecht',
    'addCarpet.valuePrice': 'Wert/Preis ($)',
    'addCarpet.valuePlaceholder': 'Wert in USD eingeben',
    'addCarpet.purchaseDate': 'Kaufdatum',
    'addCarpet.colorPalette': 'Farbpalette',
    'addCarpet.colorPlaceholder': 'z.B. Rot, Blau, Gold, Elfenbein',
    'addCarpet.patternDescription': 'Musterbeschreibung',
    'addCarpet.patternPlaceholder': 'z.B. Geometrisch, Blumig, Medaillon',
    'addCarpet.images': 'Bilder',
    'addCarpet.clickToUpload': 'Klicken Sie zum Hochladen von Bildern',
    'addCarpet.imageFormat': 'PNG, JPG, GIF bis zu 10 MB pro Bild',
    'addCarpet.upload': 'Hochladen',
    'addCarpet.notesPlaceholder': 'Zusätzliche Notizen zum Teppich...',
    'addCarpet.addingCarpet': 'Teppich wird hinzugefügt...',
    'addCarpet.addCarpet': 'Teppich hinzufügen',
    'addCarpet.internalId': 'Interne ID',
    'addCarpet.internalIdHint': '(Nur für interne Nachverfolgung)',
    'addCarpet.internalIdPlaceholder': 'Interne Referenz-ID',
    'addCarpet.materialPlaceholder': 'z.B. Wolle, Seide, Baumwolle',
    'addCarpet.failedToCreate': 'Teppich konnte nicht erstellt werden',
    'edit.title': 'Teppich bearbeiten',
    'edit.backToCarpet': 'Zurück zum Teppich',
    'edit.notFound': 'Teppich nicht gefunden',
    'edit.currentImages': 'Aktuelle Bilder',
    'edit.addNewImages': 'Neue Bilder hinzufügen',
    'edit.clickToUploadNew': 'Klicken, um neue Bilder hochzuladen',
    'edit.saveChanges': 'Änderungen speichern',
    'edit.savingChanges': 'Änderungen werden gespeichert...',
    'edit.failedToLoad': 'Teppich konnte nicht geladen werden',
    'edit.failedToUpdate': 'Teppich konnte nicht aktualisiert werden',
    'edit.failedToDeleteImage': 'Bild konnte nicht gelöscht werden',
    'search.searchingFor': 'Suche nach "{value}"...',
    'export.title': 'Exportieren',
    'export.pdf': 'Als PDF exportieren',
    'export.csv': 'Als CSV exportieren',
    'export.loading': 'Export wird vorbereitet...',
    'export.error': 'Export fehlgeschlagen',
    'export.noCarpets': 'Keine Teppiche zum Exportieren',
    'export.availableCarpets': 'Verfügbare Teppiche',
    'export.soldCarpets': 'Verkaufte Teppiche',
    'export.generated': 'Erstellt am',
    'export.page': 'Seite',
    'export.of': 'von',
    'export.total': 'Gesamt',
    'export.carpet': 'Teppich',
    'export.carpets': 'Teppiche',
    'export.headerId': 'ID',
    'export.headerName': 'Name',
    'export.headerWidth': 'Breite',
    'export.headerLength': 'Länge',
    'export.headerSoldDate': 'Verkaufsdatum',
    'auth.emailLabel': 'E-Mail-Adresse',
    'auth.emailPlaceholder': 'Geben Sie Ihre E-Mail-Adresse ein',
    'auth.passwordLabel': 'Passwort',
    'auth.passwordPlaceholder': 'Geben Sie Ihr Passwort ein',
    'auth.signIn': 'Anmelden',
    'auth.signingIn': 'Anmeldung läuft...',
    'auth.showPassword': 'Passwort anzeigen',
    'auth.hidePassword': 'Passwort verbergen',
    'auth.genericError': 'Ein Fehler ist aufgetreten',
    'delete.title': 'Teppich löschen',
    'delete.confirmMessage': 'Möchten Sie "{name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'access.checkingPermissions': 'Berechtigungen werden geprüft...',
    'access.deniedTitle': 'Zugriff verweigert',
    'access.deniedMessage': 'Sie haben keine Berechtigung für diese Funktion.',
    'access.deniedAdminOnly': 'Nur Administratoren können diese Aktion ausführen.',
    'access.deniedContact': 'Bitte wenden Sie sich an einen Administrator.',
    'access.currentRole': 'Aktuelle Rolle',
    'nav.signOutError': 'Abmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    'view.grid': 'Rasteransicht',
    'view.list': 'Listenansicht',
    'filter.categoryLabel': 'Region',
    'filter.publicationLabel': 'Veröffentlichung',
    'filter.publicationAll': 'Alle Veröffentlichungen',
    'filter.publicationPublic': 'Öffentlich',
    'filter.publicationPrivate': 'Privat',
    'search.clearSearch': 'Suche löschen',
    'error.loadingCarpets': 'Fehler beim Laden der Teppiche',
    'error.loadingSoldCarpets': 'Fehler beim Laden der verkauften Teppiche',
    'size.small': 'Klein (< 150cm)',
    'size.medium': 'Mittel (150-300cm)',
    'size.large': 'Groß (300-500cm)',
    'size.extraLarge': 'Extra Groß (> 500cm)',
    'pagination.loadMore': 'Mehr laden',
    'pagination.showingCount': '{shown} von {total} angezeigt',
    'size.smallImperial': 'Klein (< 4.9ft)',
    'size.mediumImperial': 'Mittel (4.9-9.8ft)',
    'size.largeImperial': 'Groß (9.8-16.4ft)',
    'size.extraLargeImperial': 'Extra Groß (> 16.4ft)',
    'publish.badge': 'Öffentlich',
    'publish.publish': 'Veröffentlichen',
    'publish.unpublish': 'Zurückziehen',
    'publish.confirmPublishTitle': 'Teppich veröffentlichen',
    'publish.confirmPublishMessage': '„{name}“ auf der öffentlichen Website veröffentlichen? Er bleibt dort sichtbar, solange er nicht verkauft ist. Der Rest des Bestands bleibt privat.',
    'publish.confirmUnpublishTitle': 'Veröffentlichung aufheben',
    'publish.confirmUnpublishMessage': '„{name}“ von der öffentlichen Website entfernen? Er bleibt in MC53.',
    'publish.failed': 'Veröffentlichung konnte nicht aktualisiert werden',
    'publish.removedWhenSold': 'Er wird auch von der öffentlichen Website entfernt, bis er wieder verfügbar ist.'
  },
  zh: {
    'nav.home': '首页',
    'nav.addCarpet': '添加地毯',
    'nav.soldCarpets': '已售地毯',
    'nav.signOut': '退出登录',
    'nav.signingOut': '正在退出...',
    'search.placeholder': '搜索地毯...',
    'search.noResults': '未找到地毯',
    'search.noResultsDesc': '没有与您的搜索匹配的地毯',
    'search.noResultsFilter': '没有与当前筛选条件匹配的地毯',
    'search.searching': '搜索中...',
    'search.clearFilters': '清除筛选',
    'size.all': '所有尺寸',
    'filter.allCategories': '所有地区',
    'carpet.condition': '状况',
    'carpet.material': '材质',
    'carpet.origin': '产地',
    'carpet.dimensions': '尺寸',
    'carpet.value': '价值',
    'carpet.age': '年代',
    'carpet.colors': '颜色',
    'carpet.pattern': '图案',
    'carpet.notes': '备注',
    'carpet.purchaseDate': '购买日期',
    'carpet.length': '长度',
    'carpet.width': '宽度',
    'carpet.internalId': '内部编号',
    'carpet.idLabel': '编号',
    'carpet.sizeLabel': '尺寸',
    'counter.rug': '地毯',
    'counter.rugs': '地毯',
    'auth.title': 'MC53',
    'auth.subtitle': '精选永恒地毯收藏',
    'common.loading': '加载中...',
    'common.error': '错误',
    'common.noImage': '无图片',
    'common.noImagesAvailable': '暂无图片',
    'common.backToCollection': '返回收藏',
    'common.backToMainDirectory': '返回主目录',
    'common.edit': '编辑',
    'common.delete': '删除',
    'common.cancel': '取消',
    'common.tryAgain': '重试',
    'common.added': '添加时间',
    'common.lastUpdated': '最后更新',
    'units.metric': '公制',
    'units.imperial': '英制',
    'status.sold': '已售',
    'status.markAsSold': '标记为已售',
    'status.markAsAvailable': '标记为可售',
    'status.soldCarpetsTitle': '已售地毯',
    'status.soldCarpetsDesc': '浏览已售地毯',
    'status.noSoldCarpets': '未找到已售地毯',
    'status.noSoldCarpetsDesc': '尚未有地毯被标记为已售',
    'status.confirmSoldTitle': '标记为已售',
    'status.confirmSoldMessage': '确定要将"{name}"标记为已售吗？它将从主目录中隐藏，并移至已售地毯页面。',
    'status.confirmAvailableTitle': '标记为可售',
    'status.confirmAvailableMessage': '确定要将"{name}"标记为可售吗？它将再次出现在主目录中。',
    'addCarpet.title': '添加新地毯',
    'addCarpet.backToCollection': '返回收藏',
    'addCarpet.carpetName': '地毯名称',
    'addCarpet.carpetNameRequired': '地毯名称为必填项',
    'addCarpet.originRegion': '产地/地区',
    'addCarpet.originPlaceholder': '例如：波斯、土耳其、阿富汗',
    'addCarpet.category': '类别',
    'addCarpet.categoryHint': '(主要地区 - 从名称提取)',
    'addCarpet.selectCategory': '选择类别',
    'addCarpet.widthCm': '宽度 (cm)',
    'addCarpet.widthPlaceholder': '宽度（厘米）',
    'addCarpet.lengthCm': '长度 (cm)',
    'addCarpet.lengthPlaceholder': '长度（厘米）',
    'addCarpet.agePeriod': '年代/时期',
    'addCarpet.agePlaceholder': '例如：复古、古董、现代',
    'addCarpet.selectCondition': '选择状况',
    'addCarpet.conditionExcellent': '优秀',
    'addCarpet.conditionVeryGood': '非常好',
    'addCarpet.conditionGood': '良好',
    'addCarpet.conditionFair': '一般',
    'addCarpet.conditionPoor': '较差',
    'addCarpet.valuePrice': '价值/价格 ($)',
    'addCarpet.valuePlaceholder': '输入美元价值',
    'addCarpet.purchaseDate': '购买日期',
    'addCarpet.colorPalette': '色彩搭配',
    'addCarpet.colorPlaceholder': '例如：红色、蓝色、金色、象牙色',
    'addCarpet.patternDescription': '图案描述',
    'addCarpet.patternPlaceholder': '例如：几何、花卉、奖章',
    'addCarpet.images': '图片',
    'addCarpet.clickToUpload': '点击上传图片',
    'addCarpet.imageFormat': 'PNG、JPG、GIF，每张最多10MB',
    'addCarpet.upload': '上传',
    'addCarpet.notesPlaceholder': '关于地毯的其他备注...',
    'addCarpet.addingCarpet': '正在添加地毯...',
    'addCarpet.addCarpet': '添加地毯',
    'addCarpet.internalId': '内部编号',
    'addCarpet.internalIdHint': '(仅供内部跟踪)',
    'addCarpet.internalIdPlaceholder': '内部参考编号',
    'addCarpet.materialPlaceholder': '例如：羊毛、丝绸、棉花',
    'addCarpet.failedToCreate': '创建地毯失败',
    'edit.title': '编辑地毯',
    'edit.backToCarpet': '返回地毯',
    'edit.notFound': '未找到地毯',
    'edit.currentImages': '当前图片',
    'edit.addNewImages': '添加新图片',
    'edit.clickToUploadNew': '点击上传新图片',
    'edit.saveChanges': '保存更改',
    'edit.savingChanges': '正在保存更改...',
    'edit.failedToLoad': '加载地毯失败',
    'edit.failedToUpdate': '更新地毯失败',
    'edit.failedToDeleteImage': '删除图片失败',
    'search.searchingFor': '正在搜索"{value}"...',
    'export.title': '导出',
    'export.pdf': '导出为PDF',
    'export.csv': '导出为CSV',
    'export.loading': '正在准备导出...',
    'export.error': '导出失败',
    'export.noCarpets': '没有可导出的地毯',
    'export.availableCarpets': '可售地毯',
    'export.soldCarpets': '已售地毯',
    'export.generated': '生成于',
    'export.page': '第',
    'export.of': '页，共',
    'export.total': '总计',
    'export.carpet': '地毯',
    'export.carpets': '地毯',
    'export.headerId': '编号',
    'export.headerName': '名称',
    'export.headerWidth': '宽度',
    'export.headerLength': '长度',
    'export.headerSoldDate': '售出日期',
    'auth.emailLabel': '电子邮箱',
    'auth.emailPlaceholder': '请输入电子邮箱',
    'auth.passwordLabel': '密码',
    'auth.passwordPlaceholder': '请输入密码',
    'auth.signIn': '登录',
    'auth.signingIn': '登录中...',
    'auth.showPassword': '显示密码',
    'auth.hidePassword': '隐藏密码',
    'auth.genericError': '发生错误',
    'delete.title': '删除地毯',
    'delete.confirmMessage': '确定要删除"{name}"吗？此操作无法撤销。',
    'access.checkingPermissions': '正在检查权限...',
    'access.deniedTitle': '访问被拒绝',
    'access.deniedMessage': '您没有权限使用此功能。',
    'access.deniedAdminOnly': '只有管理员可以执行此操作。',
    'access.deniedContact': '请联系管理员获取权限。',
    'access.currentRole': '当前角色',
    'nav.signOutError': '退出登录失败，请重试。',
    'view.grid': '网格视图',
    'view.list': '列表视图',
    'filter.categoryLabel': '地区',
    'filter.publicationLabel': '发布状态',
    'filter.publicationAll': '全部发布状态',
    'filter.publicationPublic': '公开',
    'filter.publicationPrivate': '未公开',
    'search.clearSearch': '清除搜索',
    'error.loadingCarpets': '加载地毯时出错',
    'error.loadingSoldCarpets': '加载已售地毯时出错',
    'size.small': '小型 (< 150cm)',
    'size.medium': '中型 (150-300cm)',
    'size.large': '大型 (300-500cm)',
    'size.extraLarge': '特大型 (> 500cm)',
    'pagination.loadMore': '加载更多',
    'pagination.showingCount': '显示 {shown} / {total}',
    'size.smallImperial': '小型 (< 4.9ft)',
    'size.mediumImperial': '中型 (4.9-9.8ft)',
    'size.largeImperial': '大型 (9.8-16.4ft)',
    'size.extraLargeImperial': '特大型 (> 16.4ft)',
    'publish.badge': '公开',
    'publish.publish': '发布',
    'publish.unpublish': '取消发布',
    'publish.confirmPublishTitle': '发布地毯',
    'publish.confirmPublishMessage': '将「{name}」发布到公开网站？只要尚未售出，它就会显示在那里。其余库存保持私密。',
    'publish.confirmUnpublishTitle': '取消发布',
    'publish.confirmUnpublishMessage': '将「{name}」从公开网站移除？它仍保留在 MC53 中。',
    'publish.failed': '无法更新发布状态',
    'publish.removedWhenSold': '它也会从公开网站移除，直到再次可供销售。'
  },
  ja: {
    'nav.home': 'ホーム',
    'nav.addCarpet': 'カーペットを追加',
    'nav.soldCarpets': '販売済みカーペット',
    'nav.signOut': 'サインアウト',
    'nav.signingOut': 'サインアウト中...',
    'search.placeholder': 'カーペットを検索...',
    'search.noResults': 'カーペットが見つかりません',
    'search.noResultsDesc': '検索に一致するカーペットがありません',
    'search.noResultsFilter': '現在のフィルターに一致するカーペットがありません',
    'search.searching': '検索中...',
    'search.clearFilters': 'フィルターをクリア',
    'size.all': 'すべてのサイズ',
    'filter.allCategories': 'すべての地域',
    'carpet.condition': '状態',
    'carpet.material': '素材',
    'carpet.origin': '原産地',
    'carpet.dimensions': '寸法',
    'carpet.value': '価値',
    'carpet.age': '年代',
    'carpet.colors': '色',
    'carpet.pattern': '模様',
    'carpet.notes': 'メモ',
    'carpet.purchaseDate': '購入日',
    'carpet.length': '長さ',
    'carpet.width': '幅',
    'carpet.internalId': '内部ID',
    'carpet.idLabel': 'ID',
    'carpet.sizeLabel': 'サイズ',
    'counter.rug': 'カーペット',
    'counter.rugs': 'カーペット',
    'auth.title': 'MC53',
    'auth.subtitle': '時代を超えたカーペットの厳選コレクション',
    'common.loading': '読み込み中...',
    'common.error': 'エラー',
    'common.noImage': '画像なし',
    'common.noImagesAvailable': '利用可能な画像がありません',
    'common.backToCollection': 'コレクションに戻る',
    'common.backToMainDirectory': 'メインディレクトリに戻る',
    'common.edit': '編集',
    'common.delete': '削除',
    'common.cancel': 'キャンセル',
    'common.tryAgain': '再試行',
    'common.added': '追加日',
    'common.lastUpdated': '最終更新',
    'units.metric': 'メートル法',
    'units.imperial': 'ヤード・ポンド法',
    'status.sold': '販売済み',
    'status.markAsSold': '販売済みにする',
    'status.markAsAvailable': '販売可能にする',
    'status.soldCarpetsTitle': '販売済みカーペット',
    'status.soldCarpetsDesc': '販売済みのカーペットを閲覧',
    'status.noSoldCarpets': '販売済みカーペットが見つかりません',
    'status.noSoldCarpetsDesc': 'まだ販売済みとマークされたカーペットはありません',
    'status.confirmSoldTitle': '販売済みにする',
    'status.confirmSoldMessage': '「{name}」を販売済みにしてもよろしいですか？メインディレクトリから非表示になり、販売済みカーペットページに移動します。',
    'status.confirmAvailableTitle': '販売可能にする',
    'status.confirmAvailableMessage': '「{name}」を販売可能にしてもよろしいですか？メインディレクトリに再表示されます。',
    'addCarpet.title': '新しいカーペットを追加',
    'addCarpet.backToCollection': 'コレクションに戻る',
    'addCarpet.carpetName': 'カーペット名',
    'addCarpet.carpetNameRequired': 'カーペット名は必須です',
    'addCarpet.originRegion': '原産地/地域',
    'addCarpet.originPlaceholder': '例：ペルシャ、トルコ、アフガン',
    'addCarpet.category': 'カテゴリー',
    'addCarpet.categoryHint': '(主要地域 - 名前から抽出)',
    'addCarpet.selectCategory': 'カテゴリーを選択',
    'addCarpet.widthCm': '幅 (cm)',
    'addCarpet.widthPlaceholder': '幅（cm）',
    'addCarpet.lengthCm': '長さ (cm)',
    'addCarpet.lengthPlaceholder': '長さ（cm）',
    'addCarpet.agePeriod': '年代/時代',
    'addCarpet.agePlaceholder': '例：ヴィンテージ、アンティーク、モダン',
    'addCarpet.selectCondition': '状態を選択',
    'addCarpet.conditionExcellent': '最高',
    'addCarpet.conditionVeryGood': 'とても良い',
    'addCarpet.conditionGood': '良い',
    'addCarpet.conditionFair': '普通',
    'addCarpet.conditionPoor': '悪い',
    'addCarpet.valuePrice': '価値/価格 ($)',
    'addCarpet.valuePlaceholder': 'USD で価値を入力',
    'addCarpet.purchaseDate': '購入日',
    'addCarpet.colorPalette': 'カラーパレット',
    'addCarpet.colorPlaceholder': '例：赤、青、金、アイボリー',
    'addCarpet.patternDescription': 'パターンの説明',
    'addCarpet.patternPlaceholder': '例：幾何学、花柄、メダリオン',
    'addCarpet.images': '画像',
    'addCarpet.clickToUpload': 'クリックして画像をアップロード',
    'addCarpet.imageFormat': 'PNG、JPG、GIF 各最大10MB',
    'addCarpet.upload': 'アップロード',
    'addCarpet.notesPlaceholder': 'カーペットに関する追加のメモ...',
    'addCarpet.addingCarpet': 'カーペットを追加中...',
    'addCarpet.addCarpet': 'カーペットを追加',
    'addCarpet.internalId': '内部ID',
    'addCarpet.internalIdHint': '(内部追跡用のみ)',
    'addCarpet.internalIdPlaceholder': '内部参照ID',
    'addCarpet.materialPlaceholder': '例：ウール、シルク、コットン',
    'addCarpet.failedToCreate': 'カーペットの作成に失敗しました',
    'edit.title': 'カーペットを編集',
    'edit.backToCarpet': 'カーペットに戻る',
    'edit.notFound': 'カーペットが見つかりません',
    'edit.currentImages': '現在の画像',
    'edit.addNewImages': '新しい画像を追加',
    'edit.clickToUploadNew': 'クリックして新しい画像をアップロード',
    'edit.saveChanges': '変更を保存',
    'edit.savingChanges': '変更を保存中...',
    'edit.failedToLoad': 'カーペットの読み込みに失敗しました',
    'edit.failedToUpdate': 'カーペットの更新に失敗しました',
    'edit.failedToDeleteImage': '画像の削除に失敗しました',
    'search.searchingFor': '「{value}」を検索中...',
    'export.title': 'エクスポート',
    'export.pdf': 'PDFとしてエクスポート',
    'export.csv': 'CSVとしてエクスポート',
    'export.loading': 'エクスポートを準備中...',
    'export.error': 'エクスポートに失敗しました',
    'export.noCarpets': 'エクスポートするカーペットがありません',
    'export.availableCarpets': '利用可能なカーペット',
    'export.soldCarpets': '販売済みカーペット',
    'export.generated': '生成日',
    'export.page': 'ページ',
    'export.of': '/',
    'export.total': '合計',
    'export.carpet': 'カーペット',
    'export.carpets': 'カーペット',
    'export.headerId': 'ID',
    'export.headerName': '名前',
    'export.headerWidth': '幅',
    'export.headerLength': '長さ',
    'export.headerSoldDate': '売却日',
    'auth.emailLabel': 'メールアドレス',
    'auth.emailPlaceholder': 'メールアドレスを入力',
    'auth.passwordLabel': 'パスワード',
    'auth.passwordPlaceholder': 'パスワードを入力',
    'auth.signIn': 'サインイン',
    'auth.signingIn': 'サインイン中...',
    'auth.showPassword': 'パスワードを表示',
    'auth.hidePassword': 'パスワードを非表示',
    'auth.genericError': 'エラーが発生しました',
    'delete.title': 'カーペットを削除',
    'delete.confirmMessage': '「{name}」を削除してもよろしいですか？この操作は元に戻せません。',
    'access.checkingPermissions': '権限を確認中...',
    'access.deniedTitle': 'アクセスが拒否されました',
    'access.deniedMessage': 'この機能を使用する権限がありません。',
    'access.deniedAdminOnly': 'この操作は管理者のみ実行できます。',
    'access.deniedContact': '管理者にお問い合わせください。',
    'access.currentRole': '現在のロール',
    'nav.signOutError': 'サインアウトに失敗しました。もう一度お試しください。',
    'view.grid': 'グリッド表示',
    'view.list': 'リスト表示',
    'filter.categoryLabel': '地域',
    'filter.publicationLabel': '公開状態',
    'filter.publicationAll': 'すべての公開状態',
    'filter.publicationPublic': '公開',
    'filter.publicationPrivate': '非公開',
    'search.clearSearch': '検索をクリア',
    'error.loadingCarpets': 'カーペットの読み込みエラー',
    'error.loadingSoldCarpets': '販売済みカーペットの読み込みエラー',
    'size.small': '小 (< 150cm)',
    'size.medium': '中 (150-300cm)',
    'size.large': '大 (300-500cm)',
    'size.extraLarge': '特大 (> 500cm)',
    'pagination.loadMore': 'さらに読み込む',
    'pagination.showingCount': '{total}件中{shown}件を表示',
    'size.smallImperial': '小 (< 4.9ft)',
    'size.mediumImperial': '中 (4.9-9.8ft)',
    'size.largeImperial': '大 (9.8-16.4ft)',
    'size.extraLargeImperial': '特大 (> 16.4ft)',
    'publish.badge': '公開',
    'publish.publish': '公開する',
    'publish.unpublish': '非公開にする',
    'publish.confirmPublishTitle': 'カーペットを公開',
    'publish.confirmPublishMessage': '「{name}」を公開サイトに掲載しますか？未販売のあいだ公開サイトに表示されます。それ以外の在庫は非公開のままです。',
    'publish.confirmUnpublishTitle': '公開を解除',
    'publish.confirmUnpublishMessage': '「{name}」を公開サイトから外しますか？MC53 には残ります。',
    'publish.failed': '公開設定を更新できませんでした',
    'publish.removedWhenSold': '再び販売可能になるまで、公開サイトからも外れます。'
  }
};

interface LocaleContextType {
  currentLocale: Locale;
  setLocale: (localeCode: string) => void;
  t: (key: string) => string;
  formatDimensions: (width: number, length: number) => string;
  formatSingleDimension: (value: number) => string;
  convertToDisplay: (cmValue: number) => { value: number; unit: string };
  getSizeLabels: () => { value: string; label: string }[];
  formatNumber: (num: number) => string;
  formatDate: (dateString: string) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLocale, setCurrentLocale] = useState<Locale>(() => {
    // Get from localStorage or default to en-US
    const savedLocale = localStorage.getItem('preferredLocale');
    return savedLocale && LOCALES[savedLocale] ? LOCALES[savedLocale] : LOCALES['en-US'];
  });

  const setLocale = (localeCode: string) => {
    if (LOCALES[localeCode]) {
      setCurrentLocale(LOCALES[localeCode]);
      localStorage.setItem('preferredLocale', localeCode);
    }
  };

  const t = (key: string): string => {
    return TRANSLATIONS[currentLocale.language]?.[key] || key;
  };



  const convertToDisplay = (cmValue: number): { value: number; unit: string } => {
    if (currentLocale.units === 'imperial') {
      // Convert to feet
      const feet = cmValue / 30.48;
      if (feet >= 1) {
        return { value: parseFloat(feet.toFixed(1)), unit: 'ft' };
      } else {
        // Convert to inches for small values
        const inches = cmValue / 2.54;
        return { value: parseFloat(inches.toFixed(1)), unit: 'in' };
      }
    } else {
      return { value: cmValue, unit: 'cm' };
    }
  };

  const formatSingleDimension = (value: number): string => {
    const { value: displayValue, unit } = convertToDisplay(value);
    return `${displayValue}${unit}`;
  };

  const formatDimensions = (width: number, length: number): string => {
    const { value: displayLength, unit } = convertToDisplay(length);
    const { value: displayWidth } = convertToDisplay(width);
    return `${displayLength}${unit} × ${displayWidth}${unit}`;
  };

  const getSizeLabels = (): { value: string; label: string }[] => {
    // Values double as the `?size=` URL parameter and must stay stable.
    const suffix = currentLocale.units === 'imperial' ? 'Imperial' : '';

    return [
      { value: 'all', label: t('size.all') },
      { value: 'small', label: t(`size.small${suffix}`) },
      { value: 'medium', label: t(`size.medium${suffix}`) },
      { value: 'large', label: t(`size.large${suffix}`) },
      { value: 'extra-large', label: t(`size.extraLarge${suffix}`) }
    ];
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat(currentLocale.code).format(num);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(currentLocale.code, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <LocaleContext.Provider value={{
      currentLocale,
      setLocale,
      t,
      formatDimensions,
      formatSingleDimension,
      convertToDisplay,
      getSizeLabels,
      formatNumber,
      formatDate
    }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}; 